/* ============================================================
   The Forever Pebble v2  concept preview
   Gate (real AES-GCM/PBKDF2 decrypt of window.FP_BLOB) + data-driven render
   + drag-and-drop workbench with a live soft-to-firm feel gauge.
   The mechanics are generic; ALL concept content arrives decrypted.
   No scroll locks anywhere: the gate is a plain fixed overlay.
   ============================================================ */
(function () {
  "use strict";
  var FP = {};
  var DATA = null;
  var state = null;

  /* ---------- base64 <-> bytes ---------- */
  function b64ToBytes(b64) {
    var bin = atob(b64);
    var out = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  /* ---------- decrypt the concept payload ---------- */
  async function decrypt(password, blob) {
    var enc = new TextEncoder();
    var salt = b64ToBytes(blob.salt);
    var iv = b64ToBytes(blob.iv);
    var ct = b64ToBytes(blob.ct);
    var km = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveKey"]);
    var key = await crypto.subtle.deriveKey(
      { name: "PBKDF2", salt: salt, iterations: blob.iter || 250000, hash: "SHA-256" },
      km,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    );
    var pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv }, key, ct); // throws on wrong password
    return JSON.parse(new TextDecoder().decode(pt));
  }

  /* ---------- gate ---------- */
  function initGate() {
    var gate = document.getElementById("fp-gate");
    var form = document.getElementById("fp-gate-form");
    var input = document.getElementById("fp-pass");
    var err = document.getElementById("fp-gate-err");
    if (!form) return;
    // stay unlocked for the viewing session
    try {
      if (sessionStorage.getItem("fp-unlocked") === "1" && window.FP_BLOB && sessionStorage.getItem("fp-pw")) {
        attempt(sessionStorage.getItem("fp-pw"), true);
      }
    } catch (e) {}

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      attempt(input.value, false);
    });

    var unlocking = false;
    async function attempt(pw, silent) {
      if (!pw || unlocking || DATA) return; // re-entrancy guard: one decrypt, one render, ever
      if (!window.crypto || !crypto.subtle) {
        err.textContent = "This browser cannot decrypt the preview. Try Chrome, Safari, or Edge.";
        return;
      }
      unlocking = true;
      if (!silent) err.textContent = "Unlocking...";
      try {
        var data = await decrypt(pw, window.FP_BLOB);
        DATA = data;
        try { sessionStorage.setItem("fp-unlocked", "1"); sessionStorage.setItem("fp-pw", pw); } catch (e) {}
        reveal(gate);
        render(data);
      } catch (e) {
        unlocking = false;
        if (!silent) { err.textContent = "That password did not work. Check with Thomas and try again."; }
        try { sessionStorage.removeItem("fp-unlocked"); sessionStorage.removeItem("fp-pw"); } catch (e2) {}
      }
    }
  }

  function reveal(gate) {
    gate.style.transition = "opacity .45s ease";
    gate.style.opacity = "0";
    setTimeout(function () { gate.remove(); }, 460);
  }

  /* ---------- helpers ---------- */
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]; }); }
  function runs(arr) { return arr.map(function (r) { return r.i ? "<em>" + esc(r.t) + "</em>" : esc(r.t); }).join(""); }
  function money(n) { return "$" + n.toLocaleString("en-US"); }
  function layer(key) { return DATA.builder.layers.find(function (l) { return l.key === key; }); }
  function opt(key, id) { var l = layer(key); return l ? l.options.find(function (o) { return o.id === id; }) : null; }
  function thNum(s) { var m = String(s).match(/[\d.]+/); return m ? parseFloat(m[0]) : 2; }

  /* ---------- price ---------- */
  function addOf(key, id) { var o = opt(key, id); return o ? (o.priceAdd || 0) : 0; }
  function comfortAdd() {
    if (state.split && state.picks.comfort && typeof state.picks.comfort === "object") {
      return Math.max(addOf("comfort", state.picks.comfort.L), addOf("comfort", state.picks.comfort.R));
    }
    return addOf("comfort", curComfort("L"));
  }
  function curComfort(side) {
    var c = state.picks.comfort;
    if (c && typeof c === "object") return c[side] || c.L;
    return c;
  }
  function price() {
    var size = DATA.builder.sizes.find(function (s) { return s.id === state.size; });
    var t = size.base;
    t += addOf("core", state.picks.core);
    t += addOf("transition", state.picks.transition);
    t += addOf("cover", state.picks.cover);
    t += comfortAdd();
    return t;
  }

  /* ---------- feel engine ---------- */
  function optFeelScore(key, id) { var o = opt(key, id); return o && typeof o.feelScore === "number" ? o.feelScore : 0; }
  function feelOf(picks) {
    var fm = DATA.builder.feelModel;
    var comfortId = (picks.comfort && typeof picks.comfort === "object") ? picks.comfort.L : picks.comfort;
    var hasBridge = picks.transition && picks.transition !== "none";
    var w = hasBridge ? fm.weights : fm.noBridgeWeights;
    var s = optFeelScore("comfort", comfortId) * w.comfort + optFeelScore("core", picks.core) * w.core;
    if (hasBridge) s += optFeelScore("transition", picks.transition) * w.transition;
    return Math.round(s * 1000) / 1000;
  }
  function feelSides() {
    if (state.split && typeof state.picks.comfort === "object") {
      return { split: true,
        L: feelOf({ comfort: state.picks.comfort.L, transition: state.picks.transition, core: state.picks.core }),
        R: feelOf({ comfort: state.picks.comfort.R, transition: state.picks.transition, core: state.picks.core }) };
    }
    return { split: false, val: feelOf(state.picks) };
  }
  function zoneOf(score) {
    var zsArr = DATA.builder.feelModel.zones;
    for (var i = 0; i < zsArr.length; i++) if (score >= zsArr[i].min && score < zsArr[i].max) return zsArr[i];
    return zsArr[zsArr.length - 1];
  }
  function gaugeSummaryText() {
    var fs = feelSides();
    if (fs.split) return "Left " + zoneOf(fs.L).name + " " + fs.L.toFixed(1) + ", right " + zoneOf(fs.R).name + " " + fs.R.toFixed(1) + " of 10";
    return zoneOf(fs.val).name + ", " + fs.val.toFixed(1) + " of 10";
  }

  /* ============================================================
     RENDER
     ============================================================ */
  function render(d) {
    var app = document.getElementById("fp-app");
    app.innerHTML = heroHTML(d) + workbenchHTML(d) + principlesHTML(d) + renewHTML(d) + circularHTML(d) + teamHTML(d) + footHTML(d) + quizHTML(d) + mobarHTML(d) +
      '<div id="fp-announce" class="visually-hidden" aria-live="polite"></div>' +
      '<div id="fp-toast" role="status"></div>' +
      '<div id="fp-pop" class="fp-pop" hidden></div>';
    app.hidden = false;
    document.body.classList.add("fp-live");
    initWorkbench();
    initQuiz();
    initReveals();
    initMobar();
    var b = document.getElementById("fp-cta-build"); if (b) b.addEventListener("click", function () { scrollToEl("fp-workbench"); });
    var w = document.getElementById("fp-cta-why"); if (w) w.addEventListener("click", function () { scrollToEl("fp-principles"); });
  }

  function heroHTML(d) {
    var h = d.hero;
    return '' +
    '<section id="fp-hero" class="fp-sec fp-sec--canvas">' +
      '<div class="fp-wrap">' +
        '<div class="fp-kicker">' + esc(h.eyebrow) + '</div>' +
        '<h1>' + runs(h.headlineRuns) + '</h1>' +
        '<p class="fp-hero-sub">' + esc(h.subhead) + '</p>' +
        '<div class="fp-hero-ctas">' +
          '<button id="fp-cta-build" class="fp-btn fp-btn--primary">' + esc(h.ctaSecondary) + '</button>' +
          '<button id="fp-cta-why" class="fp-btn fp-btn--outline">' + esc(h.cta) + '</button>' +
        '</div>' +
      '</div>' +
    '</section>';
  }

  function gaugeShellHTML(d) {
    var fm = d.builder.feelModel;
    return '<div id="fp-gauge" class="fp-gauge">' +
      '<div class="fp-gauge-top"><span id="fp-gauge-zone"></span><span id="fp-gauge-read"></span></div>' +
      '<div class="fp-gauge-track">' +
        '<span id="fp-gauge-target" hidden></span>' +
        '<span id="fp-gauge-marker" class="fp-gauge-marker"></span>' +
        '<span id="fp-gauge-marker-r" class="fp-gauge-marker fp-gauge-marker--r" hidden></span>' +
      '</div>' +
      '<div class="fp-gauge-ends"><span>' + esc(fm.softLabel) + '</span><span>' + esc(fm.firmLabel) + '</span></div>' +
      '<div id="fp-coach" class="fp-coach" hidden><span id="fp-coach-msg"></span><button id="fp-coach-back" type="button"></button></div>' +
      '<p class="fp-gauge-note">' + esc(fm.gaugeNote) + '</p>' +
    '</div>';
  }

  function workbenchHTML(d) {
    var b = d.builder;
    var wb = b.workbench;
    var presets = b.presets.map(function (p) {
      return '<button class="fp-preset" data-preset="' + esc(p.id) + '">' + esc(p.name) + ' <small>' + esc(p.desc) + '</small></button>';
    }).join("");
    return '' +
    '<section id="fp-workbench" class="fp-sec fp-sec--cream">' +
      '<div class="fp-wrap">' +
        '<div class="fp-wb-head fp-reveal">' +
          '<div class="fp-kicker">' + esc(b.eyebrow) + '</div>' +
          '<h2 class="fp-h2">' + esc(b.heading) + '</h2>' +
          '<p class="fp-lead">' + esc(b.sub) + '</p>' +
        '</div>' +
        '<div class="fp-presets"><span class="lab">' + esc(wb.presetsLabel) + '</span>' + presets +
          '<button class="fp-preset fp-preset--quiz" data-preset="__quiz">' + esc(b.quiz.cta) + ' <small>' + esc(wb.quizChipNote) + '</small></button>' +
        '</div>' +
        '<div class="fp-wb-grid">' +
          '<div class="fp-wb-left">' +
            gaugeShellHTML(d) +
            '<div class="fp-stackframe"><div class="fp-stack-cap">' + esc(b.workbench.stackHeading) + '</div>' +
              '<div id="fp-live-stack" class="fp-stack" data-drop="stack"></div>' +
              '<div id="fp-total-th"></div>' +
            '</div>' +
          '</div>' +
          '<div id="fp-shelf"></div>' +
          '<div id="fp-sumstrip"></div>' +
        '</div>' +
      '</div>' +
    '</section>';
  }

  function principlesHTML(d) {
    var p = d.principles;
    var cards = p.cards.map(function (c) {
      return '<div class="fp-card fp-reveal"><span class="num">' + esc(c.num) + '</span><h3>' + esc(c.title) + '</h3><p>' + esc(c.body) + '</p></div>';
    }).join("");
    return '' +
    '<section id="fp-principles" class="fp-sec fp-sec--canvas">' +
      '<div class="fp-wrap">' +
        '<div class="fp-reveal" style="max-width:68ch">' +
          '<div class="fp-kicker">' + esc(p.eyebrow) + '</div>' +
          '<h2 class="fp-h2">' + esc(p.heading) + '</h2>' +
          '<p class="fp-lead">' + esc(p.body) + '</p>' +
        '</div>' +
        '<div class="fp-cards">' + cards + '</div>' +
      '</div>' +
    '</section>';
  }

  function renewHTML(d) {
    var r = d.renew;
    var cards = r.cards.map(function (c) { return '<div class="fp-renew-card fp-reveal"><h3>' + esc(c.title) + '</h3><p>' + esc(c.body) + '</p></div>'; }).join("");
    return '' +
    '<section class="fp-sec fp-sec--tint">' +
      '<div class="fp-wrap">' +
        '<div class="fp-reveal" style="max-width:62ch">' +
          '<div class="fp-kicker">' + esc(r.eyebrow) + '</div>' +
          '<h2 class="fp-h2">' + esc(r.heading) + '</h2>' +
          '<p class="fp-lead">' + esc(r.body) + '</p>' +
        '</div>' +
        '<div class="fp-renew-cards">' + cards + '</div>' +
        '<p class="fp-renew-note">' + esc(r.note) + '</p>' +
      '</div>' +
    '</section>';
  }

  function circularHTML(d) {
    var c = d.circular;
    var items = c.points.map(function (p) {
      return '<div class="fp-circ-item fp-reveal"><h3>' + esc(p.title) + '</h3><p>' + esc(p.body) + '</p><cite>Source: <a href="' + esc(p.sourceUrl) + '" target="_blank" rel="noopener">' + esc(p.source) + '</a></cite></div>';
    }).join("");
    return '' +
    '<section class="fp-sec fp-sec--canvas">' +
      '<div class="fp-wrap">' +
        '<div class="fp-reveal" style="max-width:62ch">' +
          '<div class="fp-kicker">' + esc(c.eyebrow) + '</div>' +
          '<h2 class="fp-h2">' + esc(c.heading) + '</h2>' +
          '<p class="fp-lead">' + esc(c.sub) + '</p>' +
        '</div>' +
        '<div class="fp-circ-list">' + items + '</div>' +
      '</div>' +
    '</section>';
  }

  function teamHTML(d) {
    var t = d.team;
    var cases = t.case.map(function (c) { return '<div class="fp-end-card"><h4>' + esc(c.title) + '</h4><p>' + esc(c.body) + '</p></div>'; }).join("");
    var confirm = t.confirm.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join("");
    var srcs = t.sources.map(function (s) { return '<li>' + esc(s.label) + ' &rarr; <a href="' + esc(s.url) + '" target="_blank" rel="noopener">' + esc(s.url) + '</a></li>'; }).join("");
    return '' +
    '<section class="fp-sec fp-sec--end">' +
      '<div class="fp-wrap">' +
        '<span class="fp-end-tag">' + esc(t.eyebrow) + ' &middot; internal</span>' +
        '<h2 class="fp-h2">' + esc(t.heading) + '</h2>' +
        '<p class="fp-end-note">' + esc(t.note) + '</p>' +
        '<div class="fp-end-sub">' + esc(t.caseHeading) + '</div>' +
        '<div class="fp-end-grid">' + cases + '</div>' +
        '<div class="fp-end-sub">' + esc(t.confirmHeading) + '</div>' +
        '<ul class="fp-end-list">' + confirm + '</ul>' +
        '<div class="fp-end-sub">' + esc(t.sourcesHeading) + '</div>' +
        '<ul class="fp-end-links">' + srcs + '</ul>' +
      '</div>' +
    '</section>';
  }

  function footHTML(d) {
    return '<footer id="fp-foot"><div class="fp-wrap"><p class="line">' + esc(d.footer.line) + '</p><p class="sub">' + esc(d.footer.sub) + '</p></div></footer>';
  }

  function mobarHTML(d) {
    return '<div class="fp-mobar"><div class="p" id="fp-mobar-price">--<small>indicative</small></div><button class="fp-cta" id="fp-mobar-cta">' + esc(d.builder.ctaPrimary) + '</button></div>';
  }

  /* ============================================================
     WORKBENCH
     ============================================================ */
  function defaultState() {
    // no material ids live in this file; the default build comes decrypted
    return { size: DATA.builder.defaultSize, split: false, picks: {}, target: null, reserved: false };
  }

  function initWorkbench() {
    state = defaultState();
    var host = document.getElementById("fp-workbench");
    host.querySelectorAll(".fp-preset").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var id = btn.getAttribute("data-preset");
        if (id === "__quiz") { openQuiz(); return; }
        applyPreset(id);
      });
    });
    wireWorkbench(host);
    applyPreset(DATA.builder.defaultPreset); // something to play with before any quiz
    var mocta = document.getElementById("fp-mobar-cta");
    if (mocta) mocta.addEventListener("click", function () { scrollToEl("fp-workbench"); });
  }

  function applyPreset(id) {
    var p = DATA.builder.presets.find(function (x) { return x.id === id; });
    if (!p) return;
    state.picks = { core: p.picks.core, transition: p.picks.transition, comfort: p.picks.comfort, cover: p.picks.cover };
    state.split = false;
    state.target = null;
    document.querySelectorAll(".fp-preset").forEach(function (b) { b.classList.toggle("is-active", b.getAttribute("data-preset") === id); });
    afterChange();
  }
  function clearPresetActive() { document.querySelectorAll(".fp-preset").forEach(function (b) { b.classList.remove("is-active"); }); }

  function afterChange(announceMsg) {
    // keep keyboard focus across the innerHTML re-renders
    var fe = document.activeElement, fsel = null;
    if (fe && fe.getAttribute) {
      if (fe.hasAttribute("data-tile")) fsel = '[data-tile="' + fe.getAttribute("data-tile") + '"]';
      else if (fe.hasAttribute("data-info")) fsel = '[data-info="' + fe.getAttribute("data-info") + '"]';
      else if (fe.hasAttribute("data-layer")) fsel = '.fp-slab[data-layer="' + fe.getAttribute("data-layer") + '"]';
      else if (fe.hasAttribute("data-size")) fsel = '.fp-pill[data-size="' + fe.getAttribute("data-size") + '"]';
      else if (fe.getAttribute("data-role") === "splittoggle") fsel = '[data-role="splittoggle"]';
    }
    drawStack(); drawShelf(); drawGauge(); drawStrip();
    if (fsel) { var nf = document.querySelector(fsel); if (nf && nf.focus) nf.focus(); }
    if (announceMsg) { var an = document.getElementById("fp-announce"); if (an) an.textContent = announceMsg; }
  }

  /* ---------- stack ---------- */
  function slabV2(key, id) {
    var l = layer(key);
    var px = Math.round(22 + thNum(l.thickness) * 15);
    if (key === "transition" && id === "none") {
      return '<div class="fp-slab fp-slab--empty" data-drop="bridge" style="height:' + px + 'px">' + esc(DATA.builder.workbench.emptyBridge) + '</div>';
    }
    if (key === "comfort" && state.split && typeof state.picks.comfort === "object") {
      var L = opt("comfort", state.picks.comfort.L), R = opt("comfort", state.picks.comfort.R);
      return '<div class="fp-slab is-split" data-layer="comfort" role="button" tabindex="0" style="height:' + px + 'px" aria-label="Comfort layer, split left and right">' +
        '<div class="fp-slab-half fp-mat-' + esc(L.swatch) + '" data-side="L"><span class="side">L</span><span class="hname">' + esc(L.name) + '</span></div>' +
        '<div class="fp-slab-half fp-mat-' + esc(R.swatch) + '" data-side="R"><span class="side">R</span><span class="hname">' + esc(R.name) + '</span></div>' +
      '</div>';
    }
    var o = opt(key, id);
    var aria = l.name + ": " + o.name + (key === "transition" ? ". " + DATA.builder.workbench.bridgeRemoveHint : "");
    return '<div class="fp-slab fp-mat-' + esc(o.swatch) + '" data-layer="' + esc(key) + '" role="button" tabindex="0" style="height:' + px + 'px" aria-label="' + esc(aria) + '">' +
      '<span class="fp-slab-tier">' + esc(l.name) + '</span>' +
      '<span class="fp-slab-name">' + esc(o.name) + '</span>' +
      '<span class="fp-slab-th">' + esc(l.thickness) + '</span>' +
    '</div>';
  }
  function drawStack() {
    var host = document.getElementById("fp-live-stack");
    var order = ["cover", "comfort", "transition", "core"];
    host.innerHTML = order.map(function (k) {
      var id = k === "comfort" ? curComfort("L") : state.picks[k];
      return slabV2(k, id);
    }).join("");
    var total = order.reduce(function (a, k) {
      if (k === "transition" && state.picks.transition === "none") return a;
      return a + thNum(layer(k).thickness);
    }, 0);
    var tt = document.getElementById("fp-total-th");
    if (tt) tt.textContent = DATA.builder.workbench.heightPrefix + total + " in";
  }

  /* ---------- shelf ---------- */
  function isPlaced(key, id) {
    if (key === "comfort") {
      var c = state.picks.comfort;
      if (c && typeof c === "object") return c.L === id || c.R === id;
      return c === id;
    }
    return state.picks[key] === id;
  }
  function tileHTML(key, o, l) {
    var placed = isPlaced(key, o.id);
    var meta = esc(l.thickness) + " · " + (o.priceAdd > 0 ? "+" + money(o.priceAdd) : "included");
    return '<div class="fp-tile' + (placed ? " is-placed" : "") + '" data-tile="' + esc(key) + ':' + esc(o.id) + '" role="button" tabindex="0" aria-label="' + esc(o.name + ", " + l.name) + '">' +
      (placed ? '<span class="fp-tile-tag">' + esc(DATA.builder.workbench.placedTag) + '</span>' : '') +
      '<span class="fp-tile-swatch fp-mat-' + esc(o.swatch) + '"></span>' +
      '<span class="fp-tile-body"><span class="fp-tile-name">' + esc(o.name) + '</span><span class="fp-tile-meta">' + meta + '</span></span>' +
      '<span class="fp-tile-info" data-info="' + esc(key) + ':' + esc(o.id) + '" role="button" tabindex="0" aria-label="Details: ' + esc(o.name) + '">i</span>' +
    '</div>';
  }
  function drawShelf() {
    var host = document.getElementById("fp-shelf");
    var wb = DATA.builder.workbench;
    var groupOrder = ["core", "comfort", "transition", "cover"];
    var html = '<div><div class="fp-shelf-label">' + esc(wb.shelfHeading) + '</div><p class="fp-shelf-hint">' + esc(wb.shelfHint) + '</p></div>';
    groupOrder.forEach(function (key) {
      var l = layer(key);
      var opts = l.options.filter(function (o) { return !(key === "transition" && o.id === "none"); });
      html += '<div class="fp-shelf-group"><div class="fp-shelf-label">' + esc(l.name) + ' · ' + esc(l.tagline) + '</div>' +
        '<div class="fp-tiles">' + opts.map(function (o) { return tileHTML(key, o, l); }).join("") + '</div></div>';
    });
    host.innerHTML = html;
  }

  /* ---------- gauge + coach ---------- */
  function drawGauge() {
    var fm = DATA.builder.feelModel;
    var fs = feelSides();
    var m = document.getElementById("fp-gauge-marker");
    var mr = document.getElementById("fp-gauge-marker-r");
    var zoneEl = document.getElementById("fp-gauge-zone");
    var readEl = document.getElementById("fp-gauge-read");
    function pct(v) { return Math.max(2, Math.min(98, v / 10 * 100)) + "%"; }
    if (fs.split) {
      m.style.left = pct(fs.L); m.textContent = "L";
      mr.hidden = false; mr.style.left = pct(fs.R); mr.textContent = "R";
      var zL = zoneOf(fs.L).name, zR = zoneOf(fs.R).name;
      zoneEl.textContent = (zL === zR) ? zL + " / both sides" : "L " + zL + " / R " + zR;
      readEl.textContent = fs.L.toFixed(1) + " / " + fs.R.toFixed(1) + " of 10";
    } else {
      m.style.left = pct(fs.val); m.textContent = "";
      mr.hidden = true;
      zoneEl.textContent = zoneOf(fs.val).name;
      readEl.textContent = fs.val.toFixed(1) + " / 10";
    }
    var tgt = document.getElementById("fp-gauge-target");
    var coach = document.getElementById("fp-coach");
    var msgEl = document.getElementById("fp-coach-msg");
    var backBtn = document.getElementById("fp-coach-back");
    if (state.target) {
      var t = fm.targets[state.target.firmness];
      var lo = t.min / 10 * 100, hi = Math.min(t.max, 10) / 10 * 100;
      tgt.hidden = false; tgt.style.left = lo + "%"; tgt.style.width = (hi - lo) + "%";
      tgt.setAttribute("title", fm.coach.zoneLabel);
      var vals = fs.split ? [fs.L, fs.R] : [fs.val];
      var above = vals.some(function (v) { return v >= t.max; });
      var below = vals.some(function (v) { return v < t.min; });
      if (above || below) {
        msgEl.textContent = (above && below) ? fm.coach.mixed : above ? fm.coach.firmer : fm.coach.softer;
        backBtn.textContent = fm.coach.back;
        coach.hidden = false;
      } else {
        coach.hidden = true;
      }
    } else {
      tgt.hidden = true;
      coach.hidden = true;
    }
  }

  /* ---------- summary strip ---------- */
  var lastPrice = null;
  function drawStrip() {
    var host = document.getElementById("fp-sumstrip");
    var b = DATA.builder;
    var wb = b.workbench;
    var sizeObj = b.sizes.find(function (s) { return s.id === state.size; });
    var pills = b.sizes.map(function (s) {
      return '<button class="fp-pill' + (s.id === state.size ? " is-active" : "") + '" data-size="' + esc(s.id) + '">' + esc(s.name) + '</button>';
    }).join("");
    var splitCell = "";
    if (sizeObj.split) {
      splitCell = '<div class="fp-strip-cell"><span class="fp-strip-lab">' + esc(wb.splitLabel) + '</span>' +
        '<div class="fp-toggle-row"><button class="fp-toggle' + (state.split ? " is-on" : "") + '" data-role="splittoggle" role="switch" aria-checked="' + (state.split ? "true" : "false") + '" aria-label="' + esc(wb.splitLabel) + '"></button>' +
        '<span>' + esc(state.split ? wb.splitOn : wb.splitOff) + '</span></div></div>';
    }
    var order = ["cover", "comfort", "transition", "core"];
    var totalTh = order.reduce(function (a, k) { if (k === "transition" && state.picks.transition === "none") return a; return a + thNum(layer(k).thickness); }, 0);
    var fs = feelSides();
    var zoneTxt = fs.split ? (zoneOf(fs.L).name + " / " + zoneOf(fs.R).name) : zoneOf(fs.val).name;
    var reserveHTML = state.reserved
      ? '<button class="fp-cta is-done" id="fp-reserve" disabled>' + esc(b.reserveDone.button) + '</button><p class="fp-cta-note">' + esc(b.reserveDone.note) + '</p>'
      : '<button class="fp-cta" id="fp-reserve">' + esc(b.ctaPrimary) + '</button><p class="fp-cta-note" id="fp-reserve-note">' + esc(b.ctaNote) + '</p>';
    host.innerHTML =
      '<div class="fp-strip-cell"><span class="fp-strip-lab">Size</span><div class="fp-pills">' + pills + '</div></div>' +
      splitCell +
      '<div class="fp-strip-cell"><span class="fp-strip-lab">Feel</span><span class="fp-strip-fact">' + esc(zoneTxt) + '</span></div>' +
      '<div class="fp-strip-cell"><span class="fp-strip-lab">Height</span><span class="fp-strip-fact">~ ' + totalTh + ' in</span></div>' +
      '<div class="fp-strip-cell fp-total-cell"><span class="fp-strip-lab">' + esc(wb.indicativeLabel) + '</span><span class="fp-total" id="fp-total-v">' + money(price()) + '</span><p class="fp-price-note">' + esc(DATA.meta.priceNote) + '</p></div>' +
      '<div class="fp-strip-cell fp-cta-cell">' + reserveHTML + '</div>';
    var p = price();
    var v = document.getElementById("fp-total-v");
    if (v && lastPrice !== null && p !== lastPrice) { v.classList.remove("is-flash"); void v.offsetWidth; v.classList.add("is-flash"); }
    lastPrice = p;
    var mp = document.getElementById("fp-mobar-price"); if (mp) mp.innerHTML = money(p) + "<small>" + esc(wb.indicativeLabel.toLowerCase()) + "</small>";
    var res = document.getElementById("fp-reserve");
    if (res && !state.reserved) res.addEventListener("click", function () {
      state.reserved = true;
      drawStrip();
    });
  }

  /* ---------- state changes ---------- */
  function setSize(id) {
    state.size = id;
    var sizeObj = DATA.builder.sizes.find(function (s) { return s.id === id; });
    if (!sizeObj.split && state.split) {
      state.split = false;
      if (typeof state.picks.comfort === "object") state.picks.comfort = state.picks.comfort.L;
    }
    clearPresetActive();
    afterChange();
  }
  function toggleSplit() {
    state.split = !state.split;
    if (state.split && typeof state.picks.comfort !== "object") { var c = curComfort("L"); state.picks.comfort = { L: c, R: c }; }
    if (!state.split && typeof state.picks.comfort === "object") state.picks.comfort = state.picks.comfort.L;
    clearPresetActive();
    afterChange();
  }
  function place(key, id, side) {
    hidePop();
    if (key === "comfort" && state.split) {
      if (typeof state.picks.comfort !== "object") { var c = curComfort("L"); state.picks.comfort = { L: c, R: c }; }
      if (side === "L" || side === "R") state.picks.comfort[side] = id;
      else state.picks.comfort = { L: id, R: id };
    } else if (key === "comfort") {
      state.picks.comfort = id;
    } else {
      state.picks[key] = id;
    }
    clearPresetActive();
    var o = opt(key, id);
    afterChange(o.name + " added. Feel: " + gaugeSummaryText() + ". " + money(price()));
    var slab = document.querySelector('.fp-slab[data-layer="' + key + '"]');
    if (slab) slab.classList.add("fp-slab-drop");
  }
  function tapPlace(key, id, anchorEl) {
    if (key === "comfort" && state.split) { showSideChooser(id, anchorEl); return; }
    place(key, id, null);
  }
  function tryRemove(key) {
    if (key === "transition") {
      if (state.picks.transition !== "none") {
        state.picks.transition = "none";
        clearPresetActive();
        afterChange("Bridge layer removed. Feel: " + gaugeSummaryText() + ". " + money(price()));
      }
      return;
    }
    toast(DATA.builder.workbench.dragBlocked[key]);
    var slab = document.querySelector('.fp-slab[data-layer="' + key + '"]');
    if (slab) { slab.classList.remove("fp-slab-shake"); void slab.offsetWidth; slab.classList.add("fp-slab-shake"); }
  }
  function toast(msg) {
    var t = document.getElementById("fp-toast");
    if (!t) return;
    t.textContent = msg;
    t.classList.add("is-show");
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { t.classList.remove("is-show"); }, 2600);
  }

  /* ---------- popovers ---------- */
  function showPop(key, id, anchorEl) {
    var o = opt(key, id); if (!o) return;
    var dl = DATA.builder.workbench.detailLabels;
    var rows = [["feel", dl.feel], ["cool", dl.cool], ["bestFor", dl.bestFor], ["sust", dl.sust]].map(function (r) {
      return o[r[0]] ? '<div class="fp-pop-row"><b>' + esc(r[1]) + '</b> ' + esc(o[r[0]]) + '</div>' : "";
    }).join("");
    var pop = document.getElementById("fp-pop");
    pop.innerHTML = '<button class="fp-pop-x" aria-label="Close">&times;</button>' +
      '<h4>' + esc(o.name) + '</h4>' +
      '<div class="fp-pop-row">' + esc(o.material) + '</div>' +
      rows;
    positionPop(pop, anchorEl);
    pop.querySelector(".fp-pop-x").addEventListener("click", hidePop);
  }
  function showSideChooser(id, anchorEl) {
    var sc = DATA.builder.workbench.sideChooser;
    var pop = document.getElementById("fp-pop");
    pop.innerHTML = '<button class="fp-pop-x" aria-label="Close">&times;</button><h4>' + esc(sc.prompt) + '</h4>' +
      '<div class="fp-side-btns">' +
        '<button class="fp-side-btn" data-side="L">' + esc(sc.left) + '</button>' +
        '<button class="fp-side-btn" data-side="R">' + esc(sc.right) + '</button>' +
        '<button class="fp-side-btn" data-side="B">' + esc(sc.both) + '</button>' +
      '</div>';
    positionPop(pop, anchorEl);
    pop.querySelectorAll(".fp-side-btn").forEach(function (b) {
      b.addEventListener("click", function () {
        var s = b.getAttribute("data-side");
        hidePop();
        place("comfort", id, s === "B" ? null : s);
      });
    });
    pop.querySelector(".fp-pop-x").addEventListener("click", hidePop);
  }
  function positionPop(pop, anchorEl) {
    pop.hidden = false;
    var r = anchorEl.getBoundingClientRect();
    var top = r.bottom + window.scrollY + 8;
    var left = r.left + window.scrollX;
    pop.style.top = top + "px";
    pop.style.left = "0px";
    var w = pop.offsetWidth;
    var maxLeft = window.scrollX + document.documentElement.clientWidth - w - 12;
    pop.style.left = Math.max(8, Math.min(left, maxLeft)) + "px";
  }
  function hidePop() { var pop = document.getElementById("fp-pop"); if (pop) { pop.hidden = true; pop.innerHTML = ""; } }

  /* ---------- drag engine (pointer events; tap fallback built in) ---------- */
  var drag = null, holdTimer = null, touchGuard = null, ghostEl = null, dragMoved = false;

  function armDrag(el, key, id, fromStack, e) {
    if (e.button !== undefined && e.button !== 0 && e.pointerType === "mouse") return;
    if (drag) return; // one gesture at a time; a second finger does not steal the drag
    clearTimeout(holdTimer);
    dragMoved = false; // new gesture clears the click-guard
    drag = { key: key, id: id, fromStack: fromStack, src: el, pid: e.pointerId,
             sx: e.clientX, sy: e.clientY, started: false, armed: e.pointerType !== "touch" };
    if (e.pointerType === "touch") {
      holdTimer = setTimeout(function () {
        if (drag) {
          drag.armed = true;
          if (el.classList) el.classList.add("is-lifting");
          touchGuard = function (ev) { if (drag && drag.armed) ev.preventDefault(); };
          document.addEventListener("touchmove", touchGuard, { passive: false });
        }
      }, 220);
    }
    try { el.setPointerCapture && el.setPointerCapture(e.pointerId); } catch (err) {}
  }
  function makeGhost(d) {
    var o = opt(d.key, d.id);
    ghostEl = document.createElement("div");
    ghostEl.className = "fp-ghost";
    ghostEl.innerHTML = '<span class="fp-tile-swatch fp-mat-' + esc(o.swatch) + '"></span><span class="fp-tile-name">' + esc(o.name) + '</span>';
    document.body.appendChild(ghostEl);
    if (!d.fromStack) {
      var s = document.getElementById("fp-live-stack");
      if (s) s.classList.add("is-droppable");
    }
  }
  function moveGhost(x, y) { if (ghostEl) { ghostEl.style.left = (x + 14) + "px"; ghostEl.style.top = (y + 10) + "px"; } }
  function removeGhost() { if (ghostEl) { ghostEl.remove(); ghostEl = null; } }

  function onDragMove(e) {
    if (!drag || e.pointerId !== drag.pid) return;
    var dx = e.clientX - drag.sx, dy = e.clientY - drag.sy;
    if (!drag.armed) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) { clearTimeout(holdTimer); drag = null; } // it is a scroll
      return;
    }
    if (!drag.started) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      drag.started = true;
      makeGhost(drag);
      document.body.classList.add("fp-dragging");
    }
    moveGhost(e.clientX, e.clientY);
    highlightDrop(e.clientX, e.clientY);
  }
  function onDragUp(e) {
    if (!drag || e.pointerId !== drag.pid) return;
    var d = drag;
    endDrag();
    if (!d.started) {
      if (!d.fromStack) tapPlace(d.key, d.id, d.src);
      return;
    }
    dragMoved = true; // consumed by the next click, or cleared by the next armDrag
    var zone = zoneAt(e.clientX, e.clientY);
    if (d.fromStack) {
      if (!zone) tryRemove(d.key); // dragged out of the bed
      return;
    }
    if (zone) place(d.key, d.id, zone.side);
  }
  function onDragCancel() { if (drag) endDrag(); }
  function endDrag() {
    clearTimeout(holdTimer); holdTimer = null;
    if (touchGuard) { document.removeEventListener("touchmove", touchGuard); touchGuard = null; }
    if (drag && drag.src && drag.src.classList) drag.src.classList.remove("is-lifting");
    removeGhost();
    clearDropHighlights();
    document.body.classList.remove("fp-dragging");
    drag = null;
  }
  function zoneAt(x, y) {
    var el = document.elementFromPoint(x, y); // ghost is pointer-events:none, so it is skipped
    if (!el || !el.closest) return null;
    var z = el.closest('[data-drop]');
    if (!z) return null;
    var half = el.closest(".fp-slab-half");
    return { side: half ? half.getAttribute("data-side") : null };
  }
  function highlightDrop(x, y) {
    document.querySelectorAll(".is-over").forEach(function (e2) { e2.classList.remove("is-over"); });
    var el = document.elementFromPoint(x, y);
    if (!el || !el.closest || !drag || drag.fromStack) return;
    if (el.closest('[data-drop]')) {
      if (drag.key === "comfort" && state.split) { var half = el.closest(".fp-slab-half"); if (half) half.classList.add("is-over"); }
      if (drag.key === "transition") { var eb = el.closest(".fp-slab--empty"); if (eb) eb.classList.add("is-over"); }
    }
  }
  function clearDropHighlights() {
    var s = document.getElementById("fp-live-stack"); if (s) s.classList.remove("is-droppable");
    document.querySelectorAll(".is-over").forEach(function (e2) { e2.classList.remove("is-over"); });
  }

  /* ---------- wiring ---------- */
  function wireWorkbench(host) {
    host.addEventListener("click", function (e) {
      if (dragMoved) { dragMoved = false; return; } // a completed drag is not a click
      var info = e.target.closest(".fp-tile-info");
      if (info) { var ki = info.getAttribute("data-info").split(":"); showPop(ki[0], ki[1], info); return; }
      var back = e.target.closest("#fp-coach-back");
      if (back) {
        if (state.target) {
          state.picks = JSON.parse(JSON.stringify(state.target.picks));
          state.split = state.target.split;
          // the current size may forbid split; never restore into an invalid state
          var so = DATA.builder.sizes.find(function (s) { return s.id === state.size; });
          if (state.split && (!so || !so.split)) {
            state.split = false;
            if (typeof state.picks.comfort === "object") state.picks.comfort = state.picks.comfort.L;
          }
          afterChange("Back to your match. Feel: " + gaugeSummaryText() + ". " + money(price()));
        }
        return;
      }
      var pill = e.target.closest(".fp-pill[data-size]");
      if (pill) { setSize(pill.getAttribute("data-size")); return; }
      var tog = e.target.closest('[data-role="splittoggle"]');
      if (tog) { toggleSplit(); return; }
      var slab = e.target.closest(".fp-slab[data-layer]");
      if (slab) {
        var k = slab.getAttribute("data-layer");
        showPop(k, k === "comfort" ? curComfort("L") : state.picks[k], slab);
      }
    });
    host.addEventListener("keydown", function (e) {
      if (e.key === "Delete" || e.key === "Backspace") {
        var slabT = e.target.closest && e.target.closest('.fp-slab[data-layer="transition"]');
        if (slabT) { e.preventDefault(); tryRemove("transition"); }
        return;
      }
      if (e.key !== "Enter" && e.key !== " ") return;
      var info = e.target.closest(".fp-tile-info");
      if (info) { e.preventDefault(); var ki = info.getAttribute("data-info").split(":"); showPop(ki[0], ki[1], info); return; }
      var tile = e.target.closest(".fp-tile");
      if (tile) { e.preventDefault(); var kt = tile.getAttribute("data-tile").split(":"); tapPlace(kt[0], kt[1], tile); return; }
      var slab = e.target.closest(".fp-slab[data-layer]");
      if (slab) { e.preventDefault(); var k = slab.getAttribute("data-layer"); showPop(k, k === "comfort" ? curComfort("L") : state.picks[k], slab); }
    });
    host.addEventListener("pointerdown", function (e) {
      dragMoved = false; // a fresh press means the previous drag's click-guard is over
      if (e.target.closest(".fp-tile-info") || e.target.closest("#fp-pop")) return;
      var tile = e.target.closest(".fp-tile");
      if (tile) { var kt = tile.getAttribute("data-tile").split(":"); armDrag(tile, kt[0], kt[1], false, e); return; }
      var slab = e.target.closest(".fp-slab[data-layer]");
      if (slab) {
        var k = slab.getAttribute("data-layer");
        var half = e.target.closest(".fp-slab-half");
        var id = k === "comfort" ? curComfort(half ? half.getAttribute("data-side") : "L") : state.picks[k];
        armDrag(slab, k, id, true, e);
      }
    });
    if (!wireWorkbench._docWired) { // document-level listeners must never double up
      wireWorkbench._docWired = true;
      document.addEventListener("pointermove", onDragMove);
      document.addEventListener("pointerup", onDragUp);
      document.addEventListener("pointercancel", onDragCancel);
      // popover dismissal
      document.addEventListener("click", function (e) {
        var pop = document.getElementById("fp-pop");
        if (!pop || pop.hidden) return;
        if (e.target.closest("#fp-pop") || e.target.closest(".fp-tile-info") || e.target.closest(".fp-slab") || e.target.closest(".fp-tile")) return;
        hidePop();
      });
      document.addEventListener("keydown", function (e) { if (e.key === "Escape") { hidePop(); closeQuiz(); } });
    }
  }

  /* ============================================================
     QUIZ
     ============================================================ */
  var quizIdx = 0, quizAns = {};
  function quizHTML(d) {
    var q = d.builder.quiz;
    return '<div id="fp-quiz"><div class="fp-quiz-card">' +
      '<button class="fp-x" id="fp-quiz-x" aria-label="Close">&times;</button>' +
      '<div class="fp-quiz-prog" id="fp-quiz-prog"></div>' +
      '<h3 class="fp-quiz-q" id="fp-quiz-q"></h3>' +
      '<div class="fp-quiz-opts" id="fp-quiz-opts"></div>' +
      '<div class="fp-quiz-foot"><button class="fp-quiz-skip" id="fp-quiz-skip">' + esc(q.skip) + '</button><span class="fp-quiz-step" id="fp-quiz-step"></span></div>' +
    '</div></div>';
  }
  function initQuiz() {
    document.getElementById("fp-quiz-x").addEventListener("click", closeQuiz);
    document.getElementById("fp-quiz-skip").addEventListener("click", function () { closeQuiz(); scrollToEl("fp-workbench"); });
  }
  function openQuiz() { quizIdx = 0; quizAns = {}; document.getElementById("fp-quiz").classList.add("is-open"); drawQuiz(); }
  function closeQuiz() { var q = document.getElementById("fp-quiz"); if (q) q.classList.remove("is-open"); }
  function drawQuiz() {
    var qs = DATA.builder.quiz.questions;
    var q = qs[quizIdx];
    document.getElementById("fp-quiz-q").textContent = q.q;
    document.getElementById("fp-quiz-opts").innerHTML = q.opts.map(function (o) {
      return '<button class="fp-quiz-opt" data-val="' + esc(o.value) + '">' + esc(o.label) + '</button>';
    }).join("");
    document.getElementById("fp-quiz-opts").querySelectorAll(".fp-quiz-opt").forEach(function (b) {
      b.addEventListener("click", function () { quizAns[q.id] = b.getAttribute("data-val"); nextQuiz(); });
    });
    document.getElementById("fp-quiz-prog").innerHTML = qs.map(function (_, i) { return '<span class="' + (i <= quizIdx ? "done" : "") + '"></span>'; }).join("");
    document.getElementById("fp-quiz-step").textContent = "Question " + (quizIdx + 1) + " of " + qs.length;
  }
  function nextQuiz() {
    var qs = DATA.builder.quiz.questions;
    if (quizIdx < qs.length - 1) { quizIdx++; drawQuiz(); }
    else { closeQuiz(); applyRecommendation(recommend(quizAns)); scrollToEl("fp-workbench"); }
  }

  function recommend(a) {
    var firm = a.firmness || "medium";
    if (a.position === "side" && firm === "firm") firm = "medium";
    if (a.position === "stomach" && firm === "soft") firm = "medium";
    if (a.build === "heavy" && firm === "soft") firm = "medium";
    if (a.build === "heavy" && firm === "medium") firm = "firm";
    if (a.build === "light" && firm === "firm") firm = "medium";

    // the decision table (material ids) arrives decrypted; this file stays generic
    var M = DATA.builder.recoMap;
    var row = M[firm];
    var trio = (firm === "soft" && a.temp === "hot") ? row.hot
      : (firm === "medium" && a.temp === "hot" && a.position === "side") ? row.hotSide
      : (firm === "firm" && a.build === "heavy") ? row.heavy
      : row["default"];
    var cover = (a.temp === "hot") ? M.cover.hot : (a.temp === "cold") ? M.cover.cold : M.cover["default"];
    return { firmness: firm, comfort: trio[0], transition: trio[1], core: trio[2], cover: cover, couple: a.who === "couple" };
  }
  function applyRecommendation(r) {
    state.picks.comfort = r.comfort;
    state.picks.transition = r.transition;
    state.picks.core = r.core;
    state.picks.cover = r.cover;
    var sizeObj = DATA.builder.sizes.find(function (s) { return s.id === state.size; });
    if (r.couple && sizeObj && sizeObj.split) { state.split = true; state.picks.comfort = { L: r.comfort, R: r.comfort }; }
    else { state.split = false; }
    state.target = { firmness: r.firmness, picks: JSON.parse(JSON.stringify(state.picks)), split: state.split };
    clearPresetActive();
    afterChange("Your match is ready. Feel: " + gaugeSummaryText() + ". " + money(price()));
  }

  /* ============================================================
     misc
     ============================================================ */
  function scrollToEl(id) { var el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }

  function initReveals() {
    var els = document.querySelectorAll(".fp-reveal");
    if (!("IntersectionObserver" in window)) { els.forEach(function (e) { e.classList.add("is-in"); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); } });
    }, { threshold: 0.12 });
    els.forEach(function (e) { io.observe(e); });
  }

  function initMobar() {
    var b = document.getElementById("fp-workbench");
    if (!b || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { document.body.classList.toggle("fp-inbuild", en.isIntersecting); });
    }, { threshold: 0.02 });
    io.observe(b);
  }

  /* test hooks (harmless in prod; used by node + browser QA) */
  FP._test = {
    setData: function (d) { DATA = d; },
    setState: function (s) { state = s; },
    getState: function () { return state; },
    feelOf: feelOf, zoneOf: zoneOf, feelSides: feelSides, recommend: recommend,
    price: function () { return price(); }
  };

  /* boot */
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initGate);
  else initGate();
  window.FP = FP;
})();
